import { supabase } from './supabase';

export interface InstagramAccount {
  id: string;
  username: string;
  accessToken: string;
  connected: boolean;
  profilePicture?: string;
  followerCount?: number;
  followingCount?: number;
  postCount?: number;
}

export interface MockPost {
  id: string;
  imageUrl: string;
  caption: string;
  likes: number;
  comments: number;
  timestamp: string;
}

// Mock data storage
let mockInstagramAccount: InstagramAccount | null = null;
const mockPosts: MockPost[] = [
  {
    id: '1',
    imageUrl: '/api/placeholder/400/400',
    caption: 'Our latest product launch! #excited',
    likes: 124,
    comments: 12,
    timestamp: new Date(Date.now() - 86400000).toISOString() // 1 day ago
  },
  {
    id: '2',
    imageUrl: '/api/placeholder/400/400',
    caption: 'Behind the scenes at our office',
    likes: 89,
    comments: 8,
    timestamp: new Date(Date.now() - 172800000).toISOString() // 2 days ago
  }
];

export async function connectToInstagram(): Promise<void> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  mockInstagramAccount = {
    id: crypto.randomUUID(),
    username: 'your_business',
    accessToken: 'mock_token_' + Math.random().toString(36).substring(7),
    connected: true,
    profilePicture: '/api/placeholder/150/150',
    followerCount: 1234,
    followingCount: 567,
    postCount: mockPosts.length
  };
  
  // Save to Supabase (optional)
  try {
    const { error } = await supabase
      .from('instagram_accounts')
      .upsert([{
        user_id: (await supabase.auth.getUser()).data.user?.id,
        ...mockInstagramAccount
      }]);
      
    if (error) throw error;
  } catch (error) {
    console.error('Error saving Instagram account:', error);
  }
}

export async function disconnectInstagram(): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, 1000));
  mockInstagramAccount = null;
  
  // Remove from Supabase (optional)
  try {
    const { error } = await supabase
      .from('instagram_accounts')
      .delete()
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id);
      
    if (error) throw error;
  } catch (error) {
    console.error('Error removing Instagram account:', error);
  }
}

export async function getInstagramAccount(): Promise<InstagramAccount | null> {
  // Try to get from Supabase first (optional)
  try {
    const { data, error } = await supabase
      .from('instagram_accounts')
      .select('*')
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
      .single();
      
    if (data) return data;
  } catch (error) {
    console.error('Error fetching Instagram account:', error);
  }
  
  return mockInstagramAccount;
}

export async function getMockPosts(): Promise<MockPost[]> {
  await new Promise(resolve => setTimeout(resolve, 800));
  return mockPosts;
}

export async function createMockPost(caption: string): Promise<MockPost> {
  await new Promise(resolve => setTimeout(resolve, 1200));
  
  const newPost: MockPost = {
    id: crypto.randomUUID(),
    imageUrl: '/api/placeholder/400/400',
    caption,
    likes: Math.floor(Math.random() * 100),
    comments: Math.floor(Math.random() * 20),
    timestamp: new Date().toISOString()
  };
  
  mockPosts.unshift(newPost);
  if (mockInstagramAccount) {
    mockInstagramAccount.postCount = mockPosts.length;
  }
  
  return newPost;
}